import React, { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { ReactComponent as BDOIcon } from '../icons/svg/bdo-icon.svg'
import { useUserStore } from '../store/userStore'
import { useSearchParams } from 'react-router-dom'

const PasswordExpiredPage = () => {
  const { loginWithRedirect } = useAuth0()
  const user = useUserStore(state => state.user)
  const [searchParams] = useSearchParams()
  const [stateParam, setStateParam] = useState<string | null>(null)
  const isFirstLogin = searchParams.get('reason') === 'first_login'
  const isEnglish = navigator.language?.toLowerCase().startsWith('en')

  useEffect(() => {
    // Capturar el parámetro 'state' si Auth0 nos redirigió aquí desde una Action
    const state = searchParams.get('state')
    if (state) {
      setStateParam(state)
    }
  }, [searchParams])

  const handleResetPassword = async () => {
    if (stateParam) {
      // Flujo Auth0 Action:
      // Si tenemos un 'state', significa que venimos de una Action de Auth0.
      // Debemos volver a Auth0 (/continue) con ese state para que la Action
      // genere el ticket de cambio de password y redirija al usuario.
      const auth0Domain =
        window._env_?.REACT_APP_AUTH0_DOMAIN || 'bdopayroll.us.auth0.com'
      window.location.href = `https://${auth0Domain}/continue?state=${stateParam}`
    } else {
      // Flujo Fallback (Interceptor o acceso directo):
      // Si no hay state, forzamos un login nuevo.
      // Auth0 debería detectar la expiración al intentar loguearse de nuevo.
      await loginWithRedirect({
        appState: {
          returnTo: '/',
        },
        authorizationParams: {
          prompt: 'login',
          screen_hint: 'reset_password',
          login_hint: user?.email,
        },
      })
    }
  }

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{ backgroundColor: '#EEF1FC', minHeight: '100vh' }}
    >
      <Grid item xs={9} sx={{ maxHeight: '634px' }}>
        <Box
          component="main"
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexGrow: 1,
            minHeight: '100%',
            backgroundColor: 'white',
            width: '486px',
            borderRadius: '8px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Container sx={{ height: 'auto', width: '486px', py: 8 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <BDOIcon style={{ width: '150px', height: 'auto', marginBottom: '32px' }} />

              <Typography component="h1" variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                {isEnglish
                  ? isFirstLogin
                    ? 'Set up your password'
                    : 'Password Expired'
                  : isFirstLogin
                    ? 'Configurá tu contraseña'
                    : 'Contraseña Expirada'}
              </Typography>

              <Typography
                variant="body1"
                sx={{ mb: 4, textAlign: 'center', color: '#666' }}
              >
                {isEnglish
                  ? isFirstLogin
                    ? 'You received a provisional password from BDO. To access the platform, you must set your personal password before continuing.'
                    : 'Your password has expired for security reasons. Please update it to keep accessing the system.'
                  : isFirstLogin
                    ? 'Recibiste una contraseña provisoria de BDO. Para acceder a la plataforma, debés establecer tu contraseña personal antes de continuar.'
                    : 'Tu contraseña ha expirado por motivos de seguridad. Por favor, actualízala para continuar accediendo al sistema.'}
              </Typography>

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleResetPassword}
                sx={{
                  mt: 2,
                  backgroundColor: '#005EB8',
                  '&:hover': {
                    backgroundColor: '#004485',
                  },
                }}
              >
                {isEnglish
                  ? isFirstLogin
                    ? 'Set password'
                    : 'Reset password'
                  : isFirstLogin
                    ? 'Establecer contraseña'
                    : 'Restablecer Contraseña'}
              </Button>
            </Box>
          </Container>
        </Box>
      </Grid>
    </Grid>
  )
}

export default PasswordExpiredPage
